import React, { useEffect, useMemo, useRef, useState } from 'react';
import CustomDateTimePicker from '../Common/CustomDateTimePicker';
import { parseLocalDateTime, formatLocalDateTime } from '../../utils/dateTime';
import UniversalModal from '../Common/UniversalModal';
import '../Common/CardActions.css';

const DEFAULT_OFFSETS = [1, 3, 7, 14];

const formatOffsetLabel = (minutes) => {
  const value = Number(minutes || 0);
  if (!Number.isFinite(value)) return '—';
  if (value % 1440 === 0) {
    const days = value / 1440;
    if (days === 1) return 'за 1 день';
    if (days === 2 || days === 3 || days === 4) return `за ${days} дня`;
    return `за ${days} дней`;
  }
  if (value % 60 === 0) {
    return `за ${value / 60} ч.`;
  }
  return `за ${value} мин.`;
};

const buildEventOptions = (auction) => {
  const auctionType = auction?.auction_type;
  const resultsLabel = auctionType === 'public_offer' ? 'Окончание торгов' : 'Подведение итогов';
  return [
    { value: 'applications_start', label: 'Начало приема заявок' },
    { value: 'applications_end', label: 'Окончание приема заявок' },
    { value: 'auction_date', label: 'Дата и время проведения торгов' },
    { value: 'results_datetime', label: resultsLabel },
  ];
};

const ReminderFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  auctions = [],
  initialData = {},
  allowAuctionSelect = true,
  allowEventSelect = true,
}) => {
  const [auctionId, setAuctionId] = useState(initialData.auction_id || '');
  const [eventType, setEventType] = useState(initialData.event_type || '');
  const [offsetsMinutes, setOffsetsMinutes] = useState(initialData.offsets_minutes || []);
  const [customDateTime, setCustomDateTime] = useState('');
  const [customError, setCustomError] = useState('');
  const [channelEmail, setChannelEmail] = useState(initialData.channel_email ?? true);
  const [channelPush, setChannelPush] = useState(initialData.channel_push ?? true);
  const [hasChanges, setHasChanges] = useState(false);
  const [editingOffsetIdx, setEditingOffsetIdx] = useState(null);
  const [editingDateTime, setEditingDateTime] = useState('');
  const initRef = useRef(false);
  const initialSnapshotRef = useRef(null);

  const auction = useMemo(
    () => auctions.find((a) => String(a.id) === String(auctionId)),
    [auctions, auctionId]
  );

  const eventOptions = useMemo(() => buildEventOptions(auction), [auction]);
  const eventLabel = useMemo(() => {
    const match = eventOptions.find((opt) => opt.value === eventType);
    return match?.label || '—';
  }, [eventOptions, eventType]);
  const eventDateTime = useMemo(() => {
    if (!auction || !eventType) return null;
    const raw = auction[eventType];
    if (!raw) return null;
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [auction, eventType]);

  const highlightDates = useMemo(() => {
    const all = [];
    (initialData.existing_reminders || []).forEach((r) => {
      if (auctionId && String(r.auction) !== String(auctionId)) return;
      const eventDt = r.event_datetime ? new Date(r.event_datetime) : null;
      if (!eventDt || Number.isNaN(eventDt.getTime())) return;
      (r.offsets_minutes || []).forEach((m) => {
        const minutes = Number(m);
        if (!Number.isFinite(minutes)) return;
        const notifyAt = new Date(eventDt.getTime() - minutes * 60 * 1000);
        if (!Number.isNaN(notifyAt.getTime())) {
          all.push(notifyAt);
        }
      });
    });
    return all;
  }, [initialData.existing_reminders, auctionId]);

  useEffect(() => {
    if (!isOpen) return;
    setAuctionId(initialData.auction_id || '');
    setEventType(initialData.event_type || '');
    const initialOffsets = (initialData.offsets_minutes && initialData.offsets_minutes.length)
      ? initialData.offsets_minutes
      : [];
    setOffsetsMinutes(initialOffsets);
    setCustomDateTime('');
    setCustomError('');
    setChannelEmail(initialData.channel_email ?? true);
    setChannelPush(initialData.channel_push ?? true);
    setEditingOffsetIdx(null);
    setEditingDateTime('');
    setHasChanges(false);
    initRef.current = false;
    initialSnapshotRef.current = {
      auctionId: initialData.auction_id || '',
      eventType: initialData.event_type || '',
      offsets: Array.from(new Set(initialOffsets)).sort((a, b) => a - b),
      customDateTime: '',
      channelEmail: initialData.channel_email ?? true,
      channelPush: initialData.channel_push ?? true,
    };
  }, [isOpen, initialData]);

  useEffect(() => {
    if (!isOpen) return;
    if (!initRef.current) {
      initRef.current = true;
      return;
    }
    const snapshot = initialSnapshotRef.current;
    const current = {
      auctionId,
      eventType,
      offsets: Array.from(new Set(offsetsMinutes)).sort((a, b) => a - b),
      customDateTime,
      channelEmail,
      channelPush,
    };
    if (!snapshot) {
      setHasChanges(true);
      return;
    }
    setHasChanges(JSON.stringify(snapshot) !== JSON.stringify(current));
  }, [isOpen, auctionId, eventType, offsetsMinutes, customDateTime, channelEmail, channelPush]);

  const toggleOffset = (minutes) => {
    setOffsetsMinutes((prev) => {
      const set = new Set(prev);
      if (set.has(minutes)) {
        set.delete(minutes);
      } else {
        set.add(minutes);
      }
      return Array.from(set).sort((a, b) => a - b);
    });
  };

  const formatNotifyAt = (minutes) => {
    if (!eventDateTime) return '—';
    const notifyAt = new Date(eventDateTime.getTime() - Number(minutes) * 60 * 1000);
    if (Number.isNaN(notifyAt.getTime())) return '—';
    const pad = (v) => String(v).padStart(2, '0');
    return `${pad(notifyAt.getDate())}.${pad(notifyAt.getMonth() + 1)}.${notifyAt.getFullYear()} ${pad(notifyAt.getHours())}:${pad(notifyAt.getMinutes())}`;
  };

  const toInputValue = (minutes) => {
    if (!eventDateTime) return '';
    const notifyAt = new Date(eventDateTime.getTime() - Number(minutes) * 60 * 1000);
    if (Number.isNaN(notifyAt.getTime())) return '';
    return formatLocalDateTime(notifyAt).replace(',', '').trim();
  };

  const startEditOffset = (idx, minutes) => {
    setEditingOffsetIdx(idx);
    setEditingDateTime(toInputValue(minutes));
  };

  const cancelEditOffset = () => {
    setEditingOffsetIdx(null);
    setEditingDateTime('');
  };

  const saveEditOffset = () => {
    if (!eventDateTime) return;
    const notifyAt = parseLocalDateTime(editingDateTime);
    if (!notifyAt || Number.isNaN(notifyAt.getTime())) return;
    const diffMs = eventDateTime.getTime() - notifyAt.getTime();
    const diffMinutes = Math.round(diffMs / (60 * 1000));
    if (diffMinutes <= 0) return;
    setOffsetsMinutes((prev) => {
      const next = [...prev];
      if (editingOffsetIdx == null || editingOffsetIdx < 0 || editingOffsetIdx >= next.length) return prev;
      next[editingOffsetIdx] = diffMinutes;
      return Array.from(new Set(next)).sort((a, b) => a - b);
    });
    cancelEditOffset();
  };

  const removeOffset = (idx) => {
    setOffsetsMinutes((prev) => prev.filter((_, i) => i !== idx));
    if (editingOffsetIdx === idx) {
      cancelEditOffset();
    }
  };

  const addCustomOffset = () => {
    setCustomError('');
    if (!customDateTime) {
      setCustomError('Укажите дату и время напоминания.');
      return;
    }
    if (!eventDateTime) {
      setCustomError('Дата события не задана.');
      return;
    }
    const notifyAt = new Date(customDateTime);
    if (Number.isNaN(notifyAt.getTime())) {
      setCustomError('Некорректная дата.');
      return;
    }
    const diffMs = eventDateTime.getTime() - notifyAt.getTime();
    const diffMinutes = Math.round(diffMs / (60 * 1000));
    if (diffMinutes <= 0) {
      setCustomError('Напоминание должно быть до события.');
      return;
    }
    toggleOffset(diffMinutes);
    setCustomDateTime('');
  };

  const handleSubmit = () => {
    const uniqueOffsets = Array.from(new Set(offsetsMinutes)).filter((v) => Number.isFinite(v) && v > 0);
    const payload = {
      auction_id: auctionId,
      event_type: eventType,
      offsets_minutes: uniqueOffsets,
      channel_email: channelEmail,
      channel_push: channelPush,
    };
    onSubmit?.(payload);
  };

  const selectedOffsets = offsetsMinutes;
  const isSubmitDisabled = !auctionId || !eventType || !selectedOffsets.length || (!channelEmail && !channelPush);

  return (
    <UniversalModal
      isOpen={isOpen}
      onClose={onClose}
      title="Напоминание о событии"
      hasChanges={hasChanges}
      onConfirmClose={onClose}
      modalClassName="reminder-modal"
      bodyClassName="reminder-modal-body"
    >
      <div className="reminder-form">
        {allowAuctionSelect ? (
          <div className="form-group">
            <label>Торги:</label>
            <select value={auctionId} onChange={(e) => setAuctionId(e.target.value)}>
              <option value="">Выберите торги</option>
              {auctions.map((a) => (
                <option key={a.id} value={a.id}>
                  Торги №{a.id} — {a.debtor_name || a.debtor?.name || 'Должник'} ({a.auction_type})
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="form-group">
            <label>Торги:</label>
            <div>
              Торги №{auction?.id || auctionId || '—'} — {auction?.debtor_name || auction?.debtor?.name || 'Должник'}
            </div>
          </div>
        )}

        {allowEventSelect ? (
          <div className="form-group">
            <label>Событие:</label>
            <select value={eventType} onChange={(e) => setEventType(e.target.value)}>
              <option value="">Выберите событие</option>
              {eventOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="form-group">
            <label>Событие:</label>
            <div>{eventLabel}</div>
          </div>
        )}

        <div className="form-group">
          <label>Когда напомнить:</label>
          <div className="reminder-offsets">
            {DEFAULT_OFFSETS.map((days) => (
              <label key={days} className="reminder-offset-item">
                <input
                  type="checkbox"
                  checked={offsetsMinutes.includes(days * 1440)}
                  onChange={() => toggleOffset(days * 1440)}
                />
                <span>{formatOffsetLabel(days * 1440)}</span>
              </label>
            ))}
          </div>
          <div className="reminder-custom-offset">
            <div className="reminder-custom-date">
              <div style={{ marginBottom: 8, fontWeight: 600 }}>Свой срок (дата и время)</div>
              <div style={{ marginBottom: 6, fontSize: '0.85rem', color: '#6c757d' }}>
                Дни с другими напоминаниями отмечены точкой.
              </div>
              <div className="reminder-custom-row">
                <CustomDateTimePicker
                  value={customDateTime}
                  onChange={(e) => setCustomDateTime(e.target.value)}
                  label="Дата и время напоминания"
                  required={false}
                  compactCalculator={true}
                  showFlowArrows={false}
                  highlightDates={highlightDates}
                  popupPosition="center"
                  disabled={false}
                />
                <button
                  type="button"
                  className="card-action-btn card-action-btn--edit reminder-custom-action"
                  title="Добавить дату"
                  onClick={addCustomOffset}
                >
                  +
                </button>
              </div>
              {customError && (
                <div style={{ marginTop: 6, color: '#c92a2a', fontSize: '0.85rem' }}>
                  {customError}
                </div>
              )}
            </div>
          </div>
          {eventDateTime && selectedOffsets.length > 0 && (
            <div className="reminder-offsets-table">
              {selectedOffsets.map((minutes, idx) => (
                <div key={`${minutes}-${idx}`} className="reminder-offset-row">
                  <div className="reminder-offset-main">
                    <div className="reminder-offset-title">
                      {formatNotifyAt(minutes)} <span className="reminder-offset-meta">({formatOffsetLabel(minutes)})</span>
                    </div>
                    {editingOffsetIdx === idx && (
                      <input
                        type="text"
                        value={editingDateTime}
                        onChange={(e) => setEditingDateTime(e.target.value)}
                        placeholder="ДД.ММ.ГГГГ ЧЧ:ММ"
                      />
                    )}
                  </div>
                  <div className="card-actions reminder-offset-actions" style={{ justifyContent: 'flex-start' }}>
                    {editingOffsetIdx === idx ? (
                      <>
                        <button
                          type="button"
                          className="card-action-btn card-action-btn--edit"
                          title="Сохранить"
                          onClick={saveEditOffset}
                        >
                          ✓
                        </button>
                        <button
                          type="button"
                          className="card-action-btn card-action-btn--delete"
                          title="Отменить"
                          onClick={cancelEditOffset}
                        >
                          ×
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="card-action-btn card-action-btn--edit"
                          title="Изменить"
                          onClick={() => startEditOffset(idx, minutes)}
                        >
                          ✎
                        </button>
                        <button
                          type="button"
                          className="card-action-btn card-action-btn--delete"
                          title="Удалить"
                          onClick={() => removeOffset(idx)}
                        >
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Каналы:</label>
          <div className="reminder-channels">
            <label>
              <input
                type="checkbox"
                checked={channelEmail}
                onChange={(e) => setChannelEmail(e.target.checked)}
              />
              <span>Email</span>
            </label>
            <label>
              <input
                type="checkbox"
                checked={channelPush}
                onChange={(e) => setChannelPush(e.target.checked)}
              />
              <span>Push (в интерфейсе)</span>
            </label>
          </div>
        </div>

        <div className="reminder-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Отмена
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={isSubmitDisabled}>
            Сохранить напоминание
          </button>
        </div>
      </div>
    </UniversalModal>
  );
};

export default ReminderFormModal;
